/**
 * Decklines Context and Provider
 *
 * This module provides React Context for managing a Page's Decklines state throughout the Auriga application.
 * It handles fetching available Decklines data across grains, tracking the current set of Decklines in view,
 * and automatically updating the current Decklines as User modifications and interaction occur.
 *
 */

import { useDimensionNodeResolver } from '@amzn/gft-dimension-picker';
import { DimSumEnhancedNode } from '@amzn/gft-dimsum-service';
import { EXCLUDED_DECKLINES_FOR_DENOMINATORS, KPI_DIMENSION_CONFIG_MAP } from '@constants/page';
import { useAuditHistoryContext } from '@context/AuditHistoryContext';
import { useAurigaContext } from '@context/AurigaContext';
import { useDeckContext } from '@context/DeckContext';
import { useFootnotesContext } from '@context/FootnotesContext';
import { usePageContext } from '@context/PageContext';
import { useUserContext } from '@context/UserContext';
import { EnhancedComment } from '@deckManagement/components/Comments/CommentsDrawer.types';
import useKPIDecklineSync from '@hooks/useKPIDecklineSync';
import { createId } from '@paralleldrive/cuid2';
import { DecklineChangeSetType } from '@schema/Deckline.schema';
import {
  BlanklineType,
  BorderWeight,
  CalculatedKPIValues,
  CellFormat,
  CodeOrDescription,
  Deckline,
  KPIDrivers,
} from '@services/api/deckManagement/graphql/schema/API';
import { useSaveDeck, UseSaveDeckParams } from '@services/api/deckManagement/hooks';
import { useGetDecklines } from '@services/api/deckManagement/hooks/usePage';
import { entityKeyAsId, getItemIdxByInternalId, parseIdFromKey } from '@utils/deckHelpers';
import {
  applyPageDriverChangesToDecklines,
  determineDecklineType,
  determineDenominatorType,
  findSubtotalParent,
  getStartEndSubtotalRows,
  sanitizeDecklineData,
  updateDecklineDenominatorType,
  updateDecklineType,
} from '@utils/decklineHelpers.ts';
import {
  changeEntryField,
  changeMultipleEntryFields,
  initializeMetadata,
  markEntryAsDeleted,
  prepareEntriesForSaveDeckOperation,
  transformFieldsInEntries,
} from '@utils/deckManagementEntityHelpers';
import {
  generateBaseKpiId,
  generateDenominatorDriverDisplayOptions,
  generateDriverDisplayOptions,
  getEffectiveDrivers,
  hasDriverMismatch,
} from '@utils/kpiDriverHelpers.ts';
import { forEach, isEmpty, isNil, some } from 'lodash';
import { createContext, ReactNode, use, useCallback, useEffect, useMemo, useState } from 'react';
import { ChangeSetTargets, SaveDeckChangeSetSchema, SaveDeckChangeSetType } from 'src/schema';
import { CellColor } from 'types/DecklineFormatting.types';
import {
  DecklinesContextType,
  DecklinesProviderProps,
  EnhancedDeckline,
  NewDecklinePosition,
} from 'types/DecklinesContext.types';
import { DecklineRowData, PartialKPIDrivers } from 'types/Page.types';
import { DriverDisplayOptionMap } from 'types/PageContext.types';
import { KpiDimension, PageSidePanelDropDown } from 'types/PageSidePanel.types';

/**
 * Decklines Context
 *
 * React context for Decklines state management.
 */
export const DecklinesContext = createContext<DecklinesContextType>({
  pageDecklines: [],
  resolvedNodes: {},
  subtotalMap: new Map(),
  subtotalRanges: new Map(),
  setSubtotalMap: () => {},
  setSubtotalRanges: () => {},
  updatePageDeckline: () => {},
  updateDecklineComments: () => {},
  updateDecklinesForPageDriverChanges: () => {},
  insertPageDeckline: () => {},
  deletePageDeckline: () => {},
  selectedDeckline: null,
  selectedDecklineDriverOptions: {},
  updateDecklineDriverActiveState: () => {},
  updateDecklineDenominatorDriverActiveState: () => {},
  selectedDenominatorDriverOptions: {},
  decklineType: PageSidePanelDropDown.BASE_KPI,
  setDecklineType: () => {},
  updateAllDecklinesDenominatorType: () => {},
  denominatorType: PageSidePanelDropDown.BASE_KPI,
  setDenominatorType: () => {},
  chooseDeckline: () => {},
  isCreatingSubtotal: false,
  setIsCreatingSubtotal: () => {},
  repositionDeckline: () => {},
  saveDecklineChanges: () => {},
  handleSave: () => {},
  saveSubtotal: async () => {},
  isSaving: false,
  isLoading: false,
  isError: false,
  error: null,
});

/**
 * Decklines Provider Component
 *
 * This component provides Decklines Context to the application.
 * It manages the state of available Page Decklines, for a given Page Granularity selection.
 *
 * @param {DecklinesProviderProps} props - The component props for the Decklines Provider
 * @returns {JSX.Element} The Decklines provider component
 */
export const DecklinesContextProvider = ({ children }: DecklinesProviderProps): ReactNode => {
  const { username } = useUserContext();
  // Auriga General Context for Toaster and RumLogger access
  const { toastNotification, rumLogger } = useAurigaContext();
  const { generateDecklineAuditTrail, saveAuditTrail, isSavingAuditHistory } = useAuditHistoryContext();
  const { currentDeck, currentDeckId, rootNodes } = useDeckContext();
  const {
    currentGranularityPage,
    currentCorePageName,
    currentPageDriverOptions,
    selectedGranularity,
    pageDeckColumns,
    modifiedPageProperties,
  } = usePageContext();
  const { generateFootnoteChangeset } = useFootnotesContext();
  const { resolveNodes: dimSumResolveNodes } = useDimensionNodeResolver();
  // For our Save Deck operation
  const saveDeckMutation = useSaveDeck();
  // Used in increment or decrementing position as needed for addition or removal of Deckline Rows below on final resequence
  const DECKLINE_SERIAL_STEP = 1.0;
  const BLANKLINE_SERIAL_STEP = 0.1;
  const SERIAL_NO_PRECISION = 1 / BLANKLINE_SERIAL_STEP;

  const { page_drivers: currentPageKPIDrivers, denominator_label: denominatorLabel } = modifiedPageProperties || {};

  // Depend on the page's stable pk, not the object identity — currentGranularityPage's reference now
  // changes on every lock/unlock refetch (same pk, fresh lock attributes). Depending on the object
  // caused a full deckline rebuild and lost grid selection on every lock event.
  const currGranularityPagePk = currentGranularityPage?.pk;

  /**
   * State for Decklines and error handling
   * - originalPageDecklines represents the Original Raw Decklines for the current granularity Page fetched from the Api
   * - modifiedDecklines represents the Current modifiable set of Decklines in view for the current granularity Page
   */
  const [originalPageDecklines, setOriginalPageDecklines] = useState<EnhancedDeckline[]>([]);
  const [modifiedPageDecklines, setModifiedPageDecklines] = useState<EnhancedDeckline[]>([]);
  const [pageDecklinesError, setPageDecklinesError] = useState<string | null>(null);
  const [resolvedNodes, setResolvedNodes] = useState<Record<string, DimSumEnhancedNode | null>>({});
  const [subtotalMap, setSubtotalMap] = useState<Map<string, DecklineRowData[]>>(new Map());
  const [subtotalRanges, setSubtotalRanges] = useState<Map<string, { min: number; max: number }>>(new Map());
  // [2026-05-04]: isResolvingNodes is set to `true` when decklines data arrives (see setOriginalPageDecklines below)
  // to prevent a gap where isLoading briefly flips false between decklines loading and node resolution starting.
  const [isResolvingNodes, setIsResolvingNodes] = useState<boolean>(false);
  const [selectedDeckline, setSelectedDeckline] = useState<EnhancedDeckline | null>(null);
  const [selectedDecklineDriverOptions, setSelectedDecklineDriverOptionsState] =
    useState<DriverDisplayOptionMap | null>(null);
  const [selectedDenominatorDriverOptions, setSelectedDenominatorDriverOptionsState] =
    useState<DriverDisplayOptionMap | null>(null);
  const [isCreatingSubtotal, setIsCreatingSubtotal] = useState<boolean>(false);
  const [decklineType, setDecklineTypeState] = useState<PageSidePanelDropDown>(PageSidePanelDropDown.BASE_KPI);
  const [denominatorType, setDenominatorTypeState] = useState<PageSidePanelDropDown>(PageSidePanelDropDown.NONE);
  // To track saving state when a User clicks the SAVE button
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [pendingDecklineSelect, setPendingDecklineSelect] = useState<string | null>(null);

  /**
   * Reset core State values as needed
   */
  const resetDecklineData = useCallback(() => {
    setOriginalPageDecklines([]);
    setModifiedPageDecklines([]);
    setSelectedDeckline(null); // Clear selected Deckline to prevent stale selection
    setPendingDecklineSelect(null);
  }, []);

  /**
   * Function to choose/select a deckline as well as regenerating the deckline display options.
   * @param deckline - The deckline to be selected (or null to clear selection)
   */
  const chooseDeckline = useCallback(
    (deckline: EnhancedDeckline | null) => {
      setSelectedDeckline((prev) => {
        if (prev?.metadata.internalId === deckline?.metadata.internalId) {
          return prev;
        }

        setIsCreatingSubtotal(false);

        if (deckline) {
          setDecklineTypeState(determineDecklineType(deckline));
          setDenominatorTypeState(determineDenominatorType(deckline));
        }

        // Populate start and end index for subtotal rows using subtotalMap
        if (deckline && !isNil(deckline.subtotal_formula)) {
          const subtotalKey = deckline.deckline_id;
          const childRows = subtotalMap.get(subtotalKey!);
          if (childRows && childRows.length > 0) {
            const range = getStartEndSubtotalRows(childRows);
            if (range) {
              const minRow = range.min;
              const maxRow = range.max;

              setModifiedPageDecklines((prevDecklines) => {
                const decklineIndex = getItemIdxByInternalId(prevDecklines, deckline.metadata.internalId);
                if (decklineIndex !== -1) {
                  const updatedDecklines = [...prevDecklines];
                  updatedDecklines[decklineIndex] = {
                    ...updatedDecklines[decklineIndex],
                    subtotal_start_index: minRow.toString(),
                    subtotal_end_index: maxRow.toString(),
                  };
                  return updatedDecklines;
                }
                return prevDecklines;
              });
            }
          }
        }

        return deckline;
      });

      setSelectedDecklineDriverOptionsState(() => {
        if (deckline && currentPageKPIDrivers) {
          return generateDriverDisplayOptions(deckline.deckline_drivers, currentPageKPIDrivers);
        }

        return null;
      });

      setSelectedDenominatorDriverOptionsState(() => {
        if (deckline?.denominator_kpi_id_drivers && currentPageKPIDrivers) {
          return generateDenominatorDriverDisplayOptions(
            { ...deckline.deckline_drivers, ...deckline.denominator_kpi_id_drivers },
            currentPageKPIDrivers,
          );
        }

        return null;
      });
    },
    [currentPageKPIDrivers, subtotalMap],
  );

  /**
   * When pendingDecklineSelect is populated, this handles the auto-select of the new deckline.
   * NOTE: Implemented so that inserting new blanklines auto-selects the new line.
   * @see insertPageDeckline
   */
  useEffect(() => {
    if (!isEmpty(modifiedPageDecklines) && !isNil(pendingDecklineSelect)) {
      const decklineIndex = getItemIdxByInternalId(modifiedPageDecklines, pendingDecklineSelect!);
      if (decklineIndex !== -1) {
        chooseDeckline(modifiedPageDecklines[decklineIndex]);
        setPendingDecklineSelect(null);
      }
    }
  }, [chooseDeckline, modifiedPageDecklines, pendingDecklineSelect]);

  /**
   * When modifiedPageDecklines changes, refresh the selectedDeckline object to keep it synchronized.
   */
  useEffect(() => {
    setSelectedDeckline((prev) => {
      if (prev) {
        const decklineIndex = getItemIdxByInternalId(modifiedPageDecklines, prev.metadata.internalId);
        const updatedDeckline = modifiedPageDecklines[decklineIndex];

        setSelectedDecklineDriverOptionsState(
          generateDriverDisplayOptions(updatedDeckline?.deckline_drivers, currentPageKPIDrivers),
        );

        // Regenerate denominator driver options whenever the selected deckline changes,
        // returning null if the deckline has no denominator drivers configured.
        if (updatedDeckline) {
          setSelectedDenominatorDriverOptionsState(() => {
            if (updatedDeckline?.denominator_kpi_id_drivers && currentPageKPIDrivers) {
              return generateDenominatorDriverDisplayOptions(
                { ...updatedDeckline.deckline_drivers, ...updatedDeckline.denominator_kpi_id_drivers },
                currentPageKPIDrivers,
              );
            }

            return null;
          });
        }
        return updatedDeckline;
      }
      return prev;
    });
  }, [currentPageKPIDrivers, modifiedPageDecklines]);

  // Fetch associated Decklines based on the current granularity Page
  const {
    data: decklinesData,
    isLoading: isDecklinesLoading,
    isError: isDecklinesError,
    error: decklinesError,
  } = useGetDecklines({
    pageId: currGranularityPagePk ? entityKeyAsId(currGranularityPagePk) : '',
    options: {
      enabled: Boolean(currentDeckId && currGranularityPagePk && !isNil(selectedGranularity)), // Only fetch when granularity Page exists
      staleTime: 0, // Disable caching for Decklines data
      gcTime: 0,
    },
  });

  // Subscribe to KPI Library changes and sync affected decklines in real-time
  const syncPageId = currGranularityPagePk ? entityKeyAsId(currGranularityPagePk) : '';
  useKPIDecklineSync({
    enabled: Boolean(syncPageId && !isNil(selectedGranularity)),
    pageId: syncPageId,
    setModifiedPageDecklines,
    currentPageKPIDrivers: currentPageKPIDrivers ?? null,
    rootNodes,
    toastNotification,
  });

  /**
   * Effect to automatically update Decklines data based on the current granularity Page selection by a User
   * This effect runs when selectedGranularity or the current page's pk (currGranularityPagePk) changes,
   * and updates Decklines data based on the Api response.
   */
  useEffect(() => {
    const currentSubtotalMap: Map<string, DecklineRowData[]> = new Map();
    // Rebuild decklines keyed on the stable page pk (not the currentGranularityPage object identity).
    if (selectedGranularity && currGranularityPagePk && decklinesData) {
      // Handle the case of clearing State when no Decklines are returned (such as in removing the last Deckline)
      if (decklinesData.length === 0) {
        // Reset Decklines State
        resetDecklineData();
      }

      const pageId = currGranularityPagePk ? entityKeyAsId(currGranularityPagePk) : '';

      // Ensure data matches the current Page granularity selected
      const decklinesMatchPageSelection = some(decklinesData, (deckline) => deckline.pk.includes(selectedGranularity));
      if (!decklinesMatchPageSelection) {
        // Reset Decklines state
        resetDecklineData();
      } else {
        // Sort Decklines data by serial_no before further processing
        decklinesData.sort(
          (decklineA, decklineB) => parseFloat(decklineA.serial_no || '0') - parseFloat(decklineB.serial_no || '0'),
        );

        const currDecklines: EnhancedDeckline[] = [];

        // Add common metadata to the current Page decklines to aid in frontend manipulations
        forEach(decklinesData, (unsanitizedRawDeckline: Deckline, idx) => {
          const rawDeckline = sanitizeDecklineData(unsanitizedRawDeckline);
          const decklineId = parseIdFromKey(rawDeckline.sk);

          // TODO: Switch EnhancedComments to use CommonEntityMetadata `metadata` field for simplicity
          const enhancedComments = rawDeckline.comments?.map((comment): EnhancedComment => ({
            ...comment!,
            metadata: { isPendingSave: false, isPendingDelete: false },
          }));

          const preppedDeckline = {
            ...rawDeckline,
            deckline_id: decklineId, // Ensure Deckline ID is set for later use in Save Changes
            comments: enhancedComments,
            // TODO: Refactor to use these fields instead of those tracked in `.metadata` object
            deckline_type: determineDecklineType(rawDeckline),
            // kpi_name no longer contains embedded footnotes or indent spaces (those are now
            // separate fields: `footnotes` and `kpi_name_indent_level`), so actual_kpi_name === kpi_name.
            actual_kpi_name: rawDeckline.kpi_name ?? '',
            page_id: pageId,
          };

          currDecklines.push(
            initializeMetadata(preppedDeckline, {
              sortPos: idx, // Use index position as original sortPos in metadata
              // TODO: Remove once refactored (see above TODO)
              actual_kpi_name: rawDeckline.kpi_name ?? '',
              decklineType: determineDecklineType(rawDeckline),
            }),
          );
        });

        // Persist the current Page decklines to state
        setOriginalPageDecklines(currDecklines);
        setModifiedPageDecklines(currDecklines);
        setSubtotalMap(currentSubtotalMap);
        // Pre-set resolving state so isLoading doesn't briefly flip false before the node resolution effect fires
        if (currDecklines.length > 0) {
          setIsResolvingNodes(true);
        }
      }
    }
  }, [selectedGranularity, currGranularityPagePk, decklinesData, resetDecklineData]);

  /**
   * Effect to resolve RevGP nodes after decklines are loaded
   */
  useEffect(() => {
    if (originalPageDecklines.length === 0) return;

    const mapOfSegmentsToNodeDetails = new Map<KpiDimension, Map<string, string>>();

    const dimensionMaps = {
      [KpiDimension.REVGP]: new Map<string, string>(),
      [KpiDimension.ACCOUNT]: new Map<string, string>(),
      [KpiDimension.COST_CENTER]: new Map<string, string>(),
      [KpiDimension.COMPANY]: new Map<string, string>(),
      [KpiDimension.DATA_SOURCE]: new Map<string, string>(),
      [KpiDimension.PROJECT]: new Map<string, string>(),
      [KpiDimension.CHANNEL]: new Map<string, string>(),
      [KpiDimension.PRODUCT]: new Map<string, string>(),
      [KpiDimension.LOCATION]: new Map<string, string>(),
      [KpiDimension.COST_TYPE]: new Map<string, string>(),
    };

    originalPageDecklines.forEach((deckline) => {
      const kpiDecklineDrivers = getEffectiveDrivers(deckline?.deckline_drivers, currentPageKPIDrivers);

      Object.entries(KPI_DIMENSION_CONFIG_MAP).forEach(([dimension, config]) => {
        if (!config.decklineDrivers) return;

        const value = kpiDecklineDrivers?.[config.decklineDrivers.valueAccessor as keyof typeof kpiDecklineDrivers];
        const hierarchy =
          kpiDecklineDrivers?.[config.decklineDrivers.hierarchyAccessor as keyof typeof kpiDecklineDrivers];

        if (value && hierarchy) {
          const nodeId = `${hierarchy}-${value}`;
          const dimensionKey = dimension as keyof typeof dimensionMaps;
          if (dimensionMaps[dimensionKey]) {
            dimensionMaps[dimensionKey].set(nodeId, nodeId);
          }
        }
      });
    });

    Object.entries(dimensionMaps).forEach(([dimension, map]) => {
      mapOfSegmentsToNodeDetails.set(dimension as KpiDimension, map);
    });

    const nodesToResolve = Object.values(dimensionMaps).flatMap((map) => Array.from(map.values()));
    if (nodesToResolve.length > 0 && dimSumResolveNodes) {
      setIsResolvingNodes(true);

      // NOTE: setting `includeParents` to `false` allows dimSumResolveNodes to use the batch-form of the API request.
      dimSumResolveNodes(nodesToResolve, false)
        .then((response) => {
          setResolvedNodes((prev) => {
            const newResolved = { ...prev };
            response.forEach((dimSumNode) => {
              newResolved[dimSumNode.nodeID] = dimSumNode;
            });
            return newResolved;
          });
        })
        .finally(() => {
          setIsResolvingNodes(false);
        });
    } else {
      // no nodes to resolve
      setIsResolvingNodes(false);
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [originalPageDecklines, currentPageKPIDrivers]); // Removed dimSumResolveNodes from dependencies because it was causing infinite loop

  /**
   * Saves subtotal changes by updating the decklines
   */
  const saveSubtotal = useCallback((decklineItems: EnhancedDeckline[], onSuccess?: () => void) => {
    // Merge current decklines with updated ones
    setModifiedPageDecklines((prevDecklines) => {
      const updatedDecklines = [...prevDecklines];

      // Update existing decklines or add new ones
      decklineItems.forEach((updatedItem) => {
        const existingIndex = updatedDecklines.findIndex(
          (existing) => existing.metadata.internalId === updatedItem.metadata.internalId,
        );

        if (existingIndex >= 0) {
          updatedDecklines[existingIndex] = updatedItem;
        } else {
          updatedDecklines.push(updatedItem);
        }
      });

      return updatedDecklines;
    });

    // Call success callback if provided
    if (onSuccess) {
      onSuccess();
    }
  }, []);

  /**
   * Effect to clear any existing Decklines error once Decklines are successfully loaded
   */
  useEffect(() => {
    if (originalPageDecklines.length > 0 && pageDecklinesError) {
      setPageDecklinesError(null);
    }
  }, [originalPageDecklines, pageDecklinesError]);

  /**
   * Effect to handle API GetDecklines fetch errors
   */
  useEffect(() => {
    if (isDecklinesError && currGranularityPagePk && selectedGranularity) {
      setPageDecklinesError(
        `Decklines for Granularity "${selectedGranularity}" of Page "${currGranularityPagePk}" could not be fetched.`,
      );
      resetDecklineData();
    } else if (!isDecklinesError && pageDecklinesError) {
      // Clear error when API recovers
      setPageDecklinesError(null);
    }
  }, [currGranularityPagePk, isDecklinesError, pageDecklinesError, resetDecklineData, selectedGranularity]);

  /**
   * General Update Deckline handler
   *
   * Allows updating a Deckline item based on the passed in Deckline metadata internalId,
   * the field attribute to update, and the new value.
   */
  const updatePageDeckline = useCallback(
    <K extends keyof EnhancedDeckline>(internalDecklineId: string, field: K, newValue: EnhancedDeckline[K]) => {
      setModifiedPageDecklines((prevEntries) => {
        const targetDecklineItemIdx = getItemIdxByInternalId(prevEntries, internalDecklineId);
        if (targetDecklineItemIdx !== -1) {
          const newDecklineEntries = [...prevEntries];
          const targetDeckline = newDecklineEntries[targetDecklineItemIdx];

          const fieldUpdates: Partial<EnhancedDeckline> = {
            [field]: newValue,
          };

          // Update actual_kpi_name only when kpi_name changes
          if (field === 'kpi_name') {
            // When updating kpi_name from side panel, the newValue is already the clean KPI name
            // Use it directly as actual_kpi_name instead of trying to extract from combined string
            fieldUpdates.actual_kpi_name = newValue as string;
          }

          // Synchronize is_exception whenever deckline_drivers changes
          if (field === 'deckline_drivers') {
            fieldUpdates.is_exception = hasDriverMismatch(newValue as KPIDrivers | null, currentPageKPIDrivers);
          }

          const updatedDeckline = changeMultipleEntryFields(targetDeckline, fieldUpdates);

          // Update actual_kpi_name in metadata only when kpi_name changes
          if (field === 'kpi_name') {
            // When updating kpi_name from side panel, the newValue is already the clean KPI name
            // Use it directly as actual_kpi_name instead of trying to extract from combined string
            updatedDeckline.metadata.actual_kpi_name = newValue as string;
          }

          newDecklineEntries[targetDecklineItemIdx] = {
            ...updatedDeckline,
          };

          return newDecklineEntries;
        }
        // No item found for changes to be made
        return prevEntries;
      });
    },
    [setModifiedPageDecklines, currentPageKPIDrivers],
  );

  /**
   * Updates comments for a specific deckline
   * @param decklineId - The internal ID of the deckline to update
   * @param comments - The updated comments array
   */
  const updateDecklineComments = useCallback(
    (decklineId: string, comments: EnhancedComment[]) => {
      updatePageDeckline(decklineId, 'comments', comments);
    },
    [updatePageDeckline],
  );

  /**
   * Handles updating deckline drivers when Page Drivers updates.
   * @param prevPageDrivers - The previous state of Page Drivers. Use this to compare current values in deckline drivers.
   * @param updates - The updated values which need to be applied to the decklines.
   */
  const updateDecklinesForPageDriverChanges = useCallback(
    (prevPageDrivers: KPIDrivers, updates: PartialKPIDrivers) => {
      setModifiedPageDecklines((prevDecklines) =>
        applyPageDriverChangesToDecklines(prevDecklines, prevPageDrivers, updates, rootNodes),
      );
    },
    [rootNodes],
  );

  /**
   * Updates the value of `active` for a given kpiDimension (when the user toggled the <Switch> component). When
   * activating a switch, the associated value and hierarchy are reset to `null`. When deactivating a switch, this
   * restores the value/hierarchy from currentPageKPIDrivers.
   *
   * This allows users to create "Exception" decklines, where DecklineDrivers diverge from known Page Drivers.
   * @param kpiDimension - The kpiDimension to update
   * @param active - The new value of `active`
   */
  const updateDecklineDriverActiveState = useCallback(
    (kpiDimension: KpiDimension, active: boolean) => {
      if (isNil(selectedDeckline) || isNil(currentPageKPIDrivers) || isNil(currentPageDriverOptions)) {
        return;
      }

      // Prevent disabling a deckline driver option if the driver is required (according to Page Drivers)
      if (!active && !currentPageDriverOptions[kpiDimension]?.active) {
        return;
      }

      setSelectedDecklineDriverOptionsState((prev) => {
        const newDriverOptions = { ...prev };
        let resetDrivers: PartialKPIDrivers = {};
        if (newDriverOptions[kpiDimension]) {
          newDriverOptions[kpiDimension].active = active;

          // When activating, set value to empty string to signal an override without inheriting from page.
          // (null would be interpreted as "cascade from page" and immediately reset the switch)
          // When deactivating, set to null to cascade from Page Drivers.
          newDriverOptions[kpiDimension].value = active ? '' : currentPageDriverOptions[kpiDimension]?.value;
          newDriverOptions[kpiDimension].hierarchy = active ? '' : currentPageDriverOptions[kpiDimension]?.hierarchy;

          // Keep DecklineDrivers synchronized the changes above.
          const dimensionConfig = KPI_DIMENSION_CONFIG_MAP[kpiDimension];
          if (dimensionConfig) {
            const { decklineDrivers } = dimensionConfig;
            if (decklineDrivers) {
              resetDrivers = {
                [decklineDrivers.valueAccessor!]: active ? '' : null,
              };
              if (decklineDrivers?.hierarchyAccessor) {
                const hierarchy = decklineDrivers.hierarchyAccessor as keyof PartialKPIDrivers;
                resetDrivers[hierarchy] = active ? '' : null;
              }
            }
          }

          // Update the drivers
          const selectedDecklineDrivers = selectedDeckline?.deckline_drivers;
          const updatedDrivers = {
            ...(selectedDecklineDrivers || {}),
            ...resetDrivers,
          } as KPIDrivers;
          updatePageDeckline(selectedDeckline.metadata.internalId, 'deckline_drivers', updatedDrivers);

          // Update the KPI ID
          updatePageDeckline(
            selectedDeckline.metadata.internalId,
            'kpi_id',
            generateBaseKpiId(newDriverOptions, rootNodes),
          );
        }

        return newDriverOptions;
      });
    },
    [currentPageDriverOptions, currentPageKPIDrivers, rootNodes, selectedDeckline, updatePageDeckline],
  );

  /**
   * Updates the value of `active` for a given kpiDimension (when the user toggled the <Switch> component). When
   * activating a switch, the associated value and hierarchy are reset to `null`. When deactivating a switch, this
   * restores the value/hierarchy from currentPageKPIDrivers.
   *
   * @param kpiDimension - The kpiDimension to update
   * @param active - The new value of `active`
   */
  const updateDecklineDenominatorDriverActiveState = useCallback(
    (kpiDimension: KpiDimension, active: boolean) => {
      const dimensionConfig = KPI_DIMENSION_CONFIG_MAP[kpiDimension];
      const { decklineDrivers } = dimensionConfig;
      if (isNil(selectedDeckline) || isNil(dimensionConfig) || isNil(decklineDrivers) || isNil(currentPageKPIDrivers)) {
        return;
      }

      setSelectedDenominatorDriverOptionsState((prev) => {
        if (isNil(prev)) {
          return prev;
        }
        const updated = { ...prev };

        if (updated[kpiDimension]) {
          updated[kpiDimension].active = active;
          // When deactivating, restore value/hierarchy from deckline or page-level drivers
          if (!active) {
            updated[kpiDimension].value =
              selectedDeckline.deckline_drivers?.[decklineDrivers.valueAccessor] ||
              currentPageKPIDrivers[decklineDrivers.valueAccessor];
            if (decklineDrivers.hierarchyAccessor) {
              updated[kpiDimension].hierarchy =
                selectedDeckline.deckline_drivers?.[decklineDrivers.hierarchyAccessor] ||
                currentPageKPIDrivers[decklineDrivers.hierarchyAccessor];
            }
          }
        }

        return updated;
      });
    },
    [currentPageKPIDrivers, selectedDeckline],
  );

  /**
   * Get the next available serial number for a new Deckline based on the current Target reference Serial No and desired position
   * @param targetSerialNo - The target serial number to position the new Deckline relative to
   * @param position - The position to insert the new Deckline (below or above)
   */
  const getNextDecklineSerialNumber = useCallback(
    (targetSerialNo: number, position: NewDecklinePosition) => {
      let newSerialNo: number;
      if (position === NewDecklinePosition.BELOW) {
        // Below case. Increments given the defined step
        newSerialNo = targetSerialNo + BLANKLINE_SERIAL_STEP;
      } else {
        // Above case. Decrements given the defined step (allow negatives which resequencer will correct when saving)
        newSerialNo = targetSerialNo - BLANKLINE_SERIAL_STEP;
      }

      return Math.round(newSerialNo * SERIAL_NO_PRECISION) / SERIAL_NO_PRECISION; // Round to defined precision
    },
    [BLANKLINE_SERIAL_STEP, SERIAL_NO_PRECISION],
  );

  /**
   * Resequence Decklines - Assign sequential serial numbers to a list of Decklines
   * - BlankLine decklines get small increments (e.g. 0.1, 0.2, 0.3, etc.)
   * - Non-blank decklines get whole number increments (e.g. 1.0, 2.0, 3.0, etc.)
   * - Primarily used for Save Changes, to ensure all Deckline serial numbers are sequential
   * @param currDecklines - The current Decklines to resequence
   */
  const resequenceDecklines = (currDecklines: EnhancedDeckline[]): EnhancedDeckline[] => {
    // Track the last whole number for blank line positioning
    let lastWholeNumber = 0;
    // Counter for blank lines after the last whole number. Reset to 1 as needed.
    let blankLineCounter = 1;

    return currDecklines.map((deckline) => {
      const isBlankLine = determineDecklineType(deckline) === PageSidePanelDropDown.BLANK_LINE;
      let newSerialNo: number;

      // Skips rows marked for deletion when processing for resequencing, as we will not need them for numbering
      if (deckline.metadata?.isDeleted) {
        return deckline;
      }

      if (isBlankLine) {
        // BlankLine decklines get increments relative to the last whole number
        newSerialNo = lastWholeNumber + blankLineCounter * BLANKLINE_SERIAL_STEP;
        blankLineCounter += 1;
      } else {
        // Default non-Blankline case to use the next available whole number serial
        newSerialNo = lastWholeNumber + DECKLINE_SERIAL_STEP;

        // Update tracking for blank lines
        lastWholeNumber = newSerialNo;
        blankLineCounter = 1; // Reset counter for next set of blank lines
      }

      const newSerialStr = isBlankLine
        ? newSerialNo.toFixed(Math.abs(Math.log10(BLANKLINE_SERIAL_STEP)))
        : newSerialNo.toFixed(1); // Always 1 decimal place for non-decklines

      return changeEntryField(deckline, 'serial_no', newSerialStr);
    });
  };

  /**
   * Repositions a deckline after a given decklineId, or at the beginning of list.
   * @param targetDecklineId - The Deckline ID to reposition
   * @param afterDecklineId - (optional) The Deckline ID to position after. If not provided, the Deckline will be positioned at the beginning of the list
   */
  const repositionDeckline = useCallback(
    (targetDecklineId: string, afterDecklineId?: string | null) => {
      setModifiedPageDecklines((prevDecklines) => {
        const targetDecklineIndex = getItemIdxByInternalId(prevDecklines, targetDecklineId);
        if (targetDecklineIndex === -1) {
          return prevDecklines;
        }

        const afterDecklineIndex = isNil(afterDecklineId)
          ? null
          : getItemIdxByInternalId(prevDecklines, afterDecklineId);
        const newPosition = isNil(afterDecklineIndex) ? 0 : afterDecklineIndex + 1;

        // Check if new position is within a subtotal range
        const newSubtotalParent = findSubtotalParent(prevDecklines, newPosition);
        const targetDeckline = prevDecklines[targetDecklineIndex];

        // Show notification if dropping into subtotal range
        if (newSubtotalParent && newSubtotalParent !== targetDeckline.subtotal_parent) {
          toastNotification({
            message: 'Row moved into subtotal range',
            visible: true,
            type: 'warning',
          });
        }

        const newDecklines: EnhancedDeckline[] = [];
        const modifiedDeckline = changeEntryField(targetDeckline, 'subtotal_parent', newSubtotalParent);

        prevDecklines.forEach((deckline, idx) => {
          if (idx === targetDecklineIndex) {
            return;
          }

          if (isNil(afterDecklineIndex) && idx === 0) {
            // Inject the modifiedDeckline to the start of the list.
            newDecklines.push(modifiedDeckline);
            newDecklines.push(deckline);
          } else if (!isNil(afterDecklineIndex) && idx === afterDecklineIndex) {
            // Inject the modifiedDeckline after the given index position.
            newDecklines.push(deckline);
            newDecklines.push(modifiedDeckline);
          } else {
            // No change needed.
            newDecklines.push(deckline);
          }
        });

        return resequenceDecklines(newDecklines);
      });
    },
    [toastNotification],
  );

  /**
   * Updates the DecklineType of the `selectedDeckline` and updates the deckline properties related to that new type.
   * @param newType - The new Deckline type to set for the selected Deckline
   */
  const setDecklineType = useCallback(
    (newType: PageSidePanelDropDown) => {
      setDecklineTypeState((prev) => {
        if (!selectedDeckline || prev === newType) {
          return prev;
        }

        setModifiedPageDecklines((prevDecklines) => {
          const selectedDecklineIdx = getItemIdxByInternalId(prevDecklines, selectedDeckline.metadata.internalId);
          let clonedDeckline = updateDecklineType(selectedDeckline, newType, {
            currentPageKPIDrivers,
            prevDecklines,
            decklineIndex: selectedDecklineIdx,
          });

          const updatedDecklines = [...prevDecklines];
          const baseDeckline = updatedDecklines[selectedDecklineIdx - 1];
          const baseSerialNo = baseDeckline ? parseFloat(baseDeckline.serial_no || '0') : 0.0;
          const newSequenceNumber = baseSerialNo + (newType === PageSidePanelDropDown.BLANK_LINE ? 0.1 : 0.9);
          clonedDeckline = changeEntryField(clonedDeckline, 'serial_no', newSequenceNumber.toString());
          updatedDecklines[selectedDecklineIdx] = clonedDeckline;
          return updatedDecklines;
        });

        return newType;
      });
    },
    [currentPageKPIDrivers, selectedDeckline],
  );

  /**
   * Updates the DenominatorType of the `selectedDeckline` and updates the deckline properties related to that new type.
   * @param newType - The new Denominator type to set for the selected Deckline
   */
  const setDenominatorType = useCallback(
    (newType: PageSidePanelDropDown) => {
      setDenominatorTypeState((prev) => {
        if (!selectedDeckline || prev === newType) {
          return prev;
        }

        setModifiedPageDecklines((prevDecklines) => {
          const selectedDecklineIdx = getItemIdxByInternalId(prevDecklines, selectedDeckline.metadata.internalId);
          const adjustedDeckline = updateDecklineDenominatorType(
            prevDecklines[selectedDecklineIdx],
            newType,
            rootNodes,
            currentPageKPIDrivers,
          );

          const updatedDecklines = [...prevDecklines];
          updatedDecklines[selectedDecklineIdx] = adjustedDeckline;
          return updatedDecklines;
        });

        return newType;
      });
    },
    [currentPageKPIDrivers, rootNodes, selectedDeckline],
  );

  /**
   * Update the denominator types of all deckline in the current page.
   *
   * Mostly used when enabling the "Denominator Label" option in Page Settings, to establish a default Denominator Type
   * (instead of "Not Applicable")
   *
   * @param newType - The new Denominator type to set for all applicable Decklines
   */
  const updateAllDecklinesDenominatorType = useCallback(
    (newType: PageSidePanelDropDown) => {
      setModifiedPageDecklines((prevDecklines) => {
        const updatedDecklines = [...prevDecklines];
        return updatedDecklines.map((decklineEntry) => {
          // Skip decklines which should never have denominator options
          if (EXCLUDED_DECKLINES_FOR_DENOMINATORS.includes(determineDecklineType(decklineEntry))) {
            return decklineEntry;
          }

          const currentDenominatorType = determineDenominatorType(decklineEntry);

          /**
           * If we are changing to NONE, we can update any row with a valid denominator type
           * If we are changing to any other type, we only update rows currently set to NONE (to avoid overwriting existing user selections)
           */
          if (newType === PageSidePanelDropDown.NONE || currentDenominatorType === PageSidePanelDropDown.NONE) {
            return updateDecklineDenominatorType(decklineEntry, newType, rootNodes, currentPageKPIDrivers);
          }
          return decklineEntry;
        });
      });
    },
    [currentPageKPIDrivers, rootNodes],
  );

  /**
   * Insert a new Deckline at specified position relative to target row (defaults insert as BlankLine)
   * - If no target deckline and position is supplied, a new Deckline will be inserted at the top of the list
   * @param targetDecklineId - The reference Deckline internalId to position a new Deckline relative to. Optional
   * @param position - The position to insert the new Deckline (below or above). Optional
   */
  const insertPageDeckline = useCallback(
    (targetDecklineId?: string, position?: NewDecklinePosition) => {
      if (targetDecklineId && position) {
        const targetDecklineIndex = getItemIdxByInternalId(modifiedPageDecklines, targetDecklineId);
        if (targetDecklineIndex === -1) return;

        const insertIndex = position === NewDecklinePosition.BELOW ? targetDecklineIndex + 1 : targetDecklineIndex;
        const targetDeckline = modifiedPageDecklines[targetDecklineIndex];

        // Check if target deckline is within subtotal range using current decklines state
        const isTargetWithinSubtotalRange = (() => {
          // Check using subtotalMap (for saved data)
          const mapCheck = Array.from(subtotalMap.values()).some((childRows) => {
            const range = getStartEndSubtotalRows(childRows);
            if (range) {
              const { min } = range;
              const max = range.max + 1;
              return targetDecklineIndex >= min && targetDecklineIndex <= max;
            }
            return false;
          });

          // Also check current decklines for unsaved subtotal ranges and newly added rows
          const currentCheck = modifiedPageDecklines.some((deckline) => {
            // Check if current target has subtotal_parent (indicating it's in a subtotal range)
            if (targetDeckline?.subtotal_parent) return true;

            // Check if target is a subtotal row itself
            if (targetDeckline?.subtotal_formula && position === NewDecklinePosition.ABOVE) return true;

            if (deckline.subtotal_formula && !deckline.metadata.isDeleted) {
              const startIndex = parseInt(deckline.subtotal_start_index || '0', 10);
              const endIndex = parseInt(deckline.subtotal_end_index || '0', 10);
              return targetDecklineIndex >= startIndex && targetDecklineIndex <= endIndex;
            }
            return false;
          });

          return mapCheck || currentCheck;
        })();

        if (
          isTargetWithinSubtotalRange &&
          (insertIndex > targetDecklineIndex || position === NewDecklinePosition.ABOVE)
        ) {
          // Clear any existing toast first
          toastNotification({
            message: 'You are inserting row within subtotal range',
            visible: true,
            type: 'warning',
          });
        }
      }

      setModifiedPageDecklines((prevDecklines) => {
        let targetDecklineIndex = 0;
        let targetDeckline: EnhancedDeckline | undefined;
        if (targetDecklineId && position) {
          targetDecklineIndex = getItemIdxByInternalId(prevDecklines, targetDecklineId);
          if (targetDecklineIndex === -1) return prevDecklines;
          // Gets the reference Deckline details
          targetDeckline = prevDecklines[targetDecklineIndex];
        }

        // Determine insertion index
        const insertIndex = position === NewDecklinePosition.BELOW ? targetDecklineIndex + 1 : targetDecklineIndex;
        // Create a new internal tracking id for the Metadata
        const internalId = createId();
        // Formulate a Deckline ID
        const newDecklineId = `${currentCorePageName}_${selectedGranularity}-${internalId}`;

        // Generate an incremental serial_no relative to the reference Deckline as decimal part
        const currentSerialNo = parseFloat(targetDeckline?.serial_no || '0');
        // Calculate a new serial number as decimal increment/decrement relative to position, or default to a starting serial step for a Blankline
        const newSerialNo = position ? getNextDecklineSerialNumber(currentSerialNo, position) : BLANKLINE_SERIAL_STEP;

        // Check for subtotal_parent from row above
        const inheritedSubtotalParent = insertIndex > 0 ? findSubtotalParent(prevDecklines, insertIndex) : null;

        // Create a new BLANKLINE Deckline
        const newDecklineData = {
          pk: currGranularityPagePk, // To associate with the page in view. Not sent to api.
          sk: `DECKLINE#${newDecklineId}`, // Not sent to api.
          kpi_id: '|BlankLine|', // Blankline by default
          kpi_name: '',
          kpi_formula: null,
          serial_no: newSerialNo.toFixed(Math.abs(Math.log10(BLANKLINE_SERIAL_STEP))), // Set decimal places based on defined Serial Step
          is_code_or_description: CodeOrDescription.CODE,
          is_format_bold: false,
          is_format_italics: false,
          is_format_underline: false,
          format_text_color: CellColor.WHITE,
          format_cell_color: CellColor.WHITE,
          format_cell_default: CellFormat.NONE,
          blankline_type: BlanklineType.BLANKLINE, // Set as Blankline on new Deckline
          is_format_borderline: false,
          footnotes: null,
          subtotal_formula: null,
          subtotal_parent: inheritedSubtotalParent,
          is_calculated_kpi: CalculatedKPIValues.NA,
          deckline_drivers: null,
          is_exception: null,
          page_section: targetDeckline?.page_section || '1', // Use same page_section as Target reference, or default to '1'
          column_template: targetDeckline?.column_template || pageDeckColumns[0]?.column_template, // Use the same template as Target reference, or use the first available template found in DeckColumns
          adjcalcflag: false,
          exceptioncalculatedkpiflag: false,
          bordertopflag: false,
          borderrightflag: false,
          borderbottomflag: false,
          borderleftflag: false,
          borderweightflag: BorderWeight.N,
          textunderlineflag: false,
          denominator_kpi_id_for_pct: null,
          page_name: currentCorePageName || currentGranularityPage?.page_name, // Use the available Core Page Name, or the Granularity Page Name if it does not exist
          __typename: 'Deckline' as const,
          deckline_id: newDecklineId,
          page_id: currGranularityPagePk ? entityKeyAsId(currGranularityPagePk) : '',
        };

        // Initialize the new Deckline with Metadata and sortPos
        const newDeckline: EnhancedDeckline = updateDecklineType(
          initializeMetadata(newDecklineData, {
            isNewRow: true,
            sortPos: insertIndex,
          }),
          PageSidePanelDropDown.BLANK_LINE,
        );

        // Insert at correct array position without resequencing for any collisions (final resequencing done before Save Changes)
        const newDecklines = [...prevDecklines];
        newDecklines.splice(insertIndex, 0, newDeckline);

        // Assign pendingDecklineSelect, so that we auto-select the new deckline once modifiedDecklines is updated.
        setPendingDecklineSelect(newDeckline.metadata.internalId);
        return newDecklines;
      });
    },
    [
      modifiedPageDecklines,
      subtotalMap,
      toastNotification,
      currentCorePageName,
      selectedGranularity,
      getNextDecklineSerialNumber,
      currGranularityPagePk,
      currentGranularityPage?.page_name,
      pageDeckColumns,
    ],
  );

  /**
   * Delete a Deckline from the current Page, given a target Deckline internalId
   *
   * Marks the Deckline as deleted in the modifiedPageDecklines state.
   * This change can be saved or discarded.
   *
   * @param targetDecklineId - The Deckline internalId to delete
   */
  const deletePageDeckline = useCallback(
    (targetDecklineId: string) => {
      setModifiedPageDecklines((prevDecklines) => {
        const targetDecklineIndex = getItemIdxByInternalId(prevDecklines, targetDecklineId);
        if (targetDecklineIndex === -1) return prevDecklines;

        // Mark the target Deckline as Deleted at the index before saving,
        // or remove the item in the case it was a Deckline pending actual Creation, that is now marked for Deletion
        const newDecklines = [...prevDecklines];
        if (newDecklines[targetDecklineIndex].metadata.isNewRow) {
          // Deckline was pending Creation also, so remove it from the Decklines as User wants to Delete it
          newDecklines.splice(targetDecklineIndex, 1);
        } else {
          // Flag the deckline for Deletion during Save Changes
          newDecklines[targetDecklineIndex] = markEntryAsDeleted(newDecklines[targetDecklineIndex]);
        }

        return newDecklines;
      });
      // Reset the selected deckline state as we are marking the row as deleted
      setSelectedDeckline(null);
    },
    [setModifiedPageDecklines],
  );

  /**
   * Saves all footnote changes
   *
   * @param {() => void} successCallback - Optional callback to execute after successful save
   * @param {() => void} errorCallback - Optional callback to execute after an error occurs.
   */
  const saveFootnotesChanges = useCallback(
    (successCallback?: () => void, errorCallback?: () => void) => {
      setIsSaving(true);

      const { createdFootnotes, updatedFootnotes, deletedFootnotes } = generateFootnoteChangeset();

      if (!isEmpty(createdFootnotes) || !isEmpty(updatedFootnotes) || !isEmpty(deletedFootnotes)) {
        const footnoteChangeSet = {
          [ChangeSetTargets.footnote]: {
            create: createdFootnotes,
            update: updatedFootnotes,
            delete: deletedFootnotes,
          },
        };

        try {
          const validatedFootnotesChangeSet: SaveDeckChangeSetType = SaveDeckChangeSetSchema.parse(footnoteChangeSet);
          const saveFootnotesParams: UseSaveDeckParams = {
            deckId: currentDeckId || undefined,
            deckName: currentDeck?.deck_name || undefined,
            change_set: validatedFootnotesChangeSet,
            currentUser: username,
            successCallback: async () => {
              setIsSaving(false);
              toastNotification({
                message: 'Footnote changes saved',
                visible: true,
                type: 'info',
              });
              if (successCallback) successCallback();
            },
          };

          saveDeckMutation.mutate(saveFootnotesParams, {
            onError: (saveError) => {
              setIsSaving(false);
              console.error('Footnote save failed:', saveError);
              toastNotification({
                message: 'Save Footnotes Error',
                visible: true,
                type: 'error',
              });
              errorCallback?.();
            },
          });
        } catch (validationError) {
          console.error('Footnote changeset validation failed', validationError);
          setIsSaving(false);
          errorCallback?.();
        }
      } else {
        setIsSaving(false);
        if (successCallback) successCallback();
      }
    },
    [currentDeck?.deck_name, currentDeckId, generateFootnoteChangeset, saveDeckMutation, toastNotification, username],
  );

  /**
   * Saves all Deckline changes
   * Saves all changes made to the Deckline entries for a Page in the backend.
   * After saving, the originalPageDecklines state is updated to match the modifiedPageDecklines state.
   *
   * @param {() => void} successCallback - Optional callback to execute on a successful save from the calling component
   * @param {() => void} errorCallback - Optional callback to execute after an error occurs.
   */
  const saveDecklineChanges = useCallback(
    (successCallback?: () => void, errorCallback?: () => void) => {
      setIsSaving(true);

      // Tracks start time of SaveDeck request for RUM tracking
      const saveStartTime = Date.now();

      // Resequence the existing ModifiedDecklines before saving
      const resequencedModDecklines = resequenceDecklines(modifiedPageDecklines);

      // For use in API requests as required, as an addition to EnhancedDeckline definition in EnhancedDecklineWithPageId
      const pageId = currGranularityPagePk ? entityKeyAsId(currGranularityPagePk) : '';

      // Transform the entries to filter out deleted comments
      const transformedDecklines = transformFieldsInEntries(resequencedModDecklines, {
        comments: (entry) => entry.comments?.filter((c) => !c?.metadata.isPendingDelete),
      });

      // Filter and transform the decklines into relevant create/delete/update lists for SaveDeck.
      const preparedEntries = prepareEntriesForSaveDeckOperation(transformedDecklines, []);

      const saveDeckChangeSet: SaveDeckChangeSetType = {};

      // Collect any changes for the Page object
      if (modifiedPageProperties?.metadata.isModified) {
        saveDeckChangeSet[ChangeSetTargets.page] = {
          update: [
            {
              page_id: pageId,
              page_drivers: { ...currentPageKPIDrivers },
              denominator_label: denominatorLabel,
            },
          ],
        };
      }

      // Collect any changes to the Deckline objects
      if (Object.values(preparedEntries).some((entries) => entries.length > 0)) {
        // Final formatting of the Deckline change_set based on the separated collections
        saveDeckChangeSet[ChangeSetTargets.deckline] = preparedEntries as DecklineChangeSetType;
      }

      // Process the api call only if there are actual changes to save
      if (Object.keys(saveDeckChangeSet).length > 0) {
        // Send for API handling of SaveDeck
        try {
          const validatedSaveDeckChangeSet: SaveDeckChangeSetType = SaveDeckChangeSetSchema.parse(saveDeckChangeSet);
          // Create a payload with all the changes
          const saveDeckParams: UseSaveDeckParams = {
            deckId: currentDeckId || undefined,
            deckName: currentDeck?.deck_name || undefined,
            change_set: validatedSaveDeckChangeSet,
            currentUser: username,
            successCallback: async () => {
              // Record successful save operation
              rumLogger?.recordApiLatency('DeckManagement', 'SaveDeck', saveStartTime, true);

              // Generate Audit History Trail and Save Audit History
              const auditTrail = generateDecklineAuditTrail(
                originalPageDecklines,
                preparedEntries.create,
                preparedEntries.update,
                preparedEntries.delete,
              );
              // Persist the generated Audit Trail
              saveAuditTrail(auditTrail);

              // Add a small delay for cache invalidation and refetch to complete before showing success
              setTimeout(() => {
                // Clear local isSaving state
                setIsSaving(false);

                // Call the prop successCallback
                if (successCallback) successCallback();
              }, 100);
            },
          };

          saveDeckMutation.mutate(saveDeckParams, {
            onError: (saveError) => {
              // Record failed save operation
              rumLogger?.recordApiLatency('DeckManagement', 'SaveDeck', saveStartTime, false);
              setIsSaving(false);
              console.error('Save operation failed:', saveError);
              toastNotification({
                message: 'Save Deck Error Has Occurred',
                visible: true,
                type: 'error',
              });
              errorCallback?.();
            },
          });
        } catch (validationError) {
          console.error('SaveDeck changeset validation failed', validationError);
          errorCallback?.();
        }
      } else {
        // No changes to save — use errorCallback to reset caller state without triggering success toast
        setIsSaving(false);
        toastNotification({
          message: 'No Changes to Save',
          visible: true,
          type: 'info',
        });
        errorCallback?.();
      }
    },
    [
      currentDeck?.deck_name,
      currentDeckId,
      currGranularityPagePk,
      currentPageKPIDrivers,
      denominatorLabel,
      generateDecklineAuditTrail,
      modifiedPageDecklines,
      modifiedPageProperties?.metadata.isModified,
      originalPageDecklines,
      rumLogger,
      saveAuditTrail,
      saveDeckMutation,
      toastNotification,
      username,
    ],
  );

  /**
   * Handles the save operation order: footnotes first, then decklines
   *
   * @param {() => void} successCallback - Optional callback to execute after all saves complete
   * @param {() => void} errorCallback - Optional callback to execute after an error occurs.
   */
  const handleSave = useCallback(
    (successCallback?: () => void, errorCallback?: () => void) => {
      saveFootnotesChanges(() => {
        saveDecklineChanges(successCallback, errorCallback);
      }, errorCallback);
    },
    [saveFootnotesChanges, saveDecklineChanges],
  );

  // Filter Decklines with consideration for modifications in the current view (.e.g. Deckline deletions)
  const filteredDecklines = useMemo(() => {
    return modifiedPageDecklines.filter((deckline) => {
      if (!deckline) return false;
      // Exclude deleted entries from displaying
      return !deckline.metadata.isDeleted;
    });
  }, [modifiedPageDecklines]);

  // Combine loading states — include "data received but not yet processed" to prevent flicker
  const isLoading =
    isDecklinesLoading || isResolvingNodes || (!isEmpty(decklinesData) && isEmpty(modifiedPageDecklines));

  // Combine save states to include audit history action
  const isDecklinesSaving = isSaving || isSavingAuditHistory;

  // Combine error states (expand as needed)
  const isError = isDecklinesError || !!pageDecklinesError;
  const error = pageDecklinesError || (isDecklinesError ? decklinesError : null);

  /**
   * Creates the DecklinesContext value object
   *
   * Combines all state variables and methods into a single object that will be
   * provided to consumers of the context. This ensures that the context value
   * only changes when one of its dependencies changes, preventing unnecessary
   * re-renders of components that consume the context.
   */
  const contextValue = useMemo<DecklinesContextType>(
    () => ({
      pageDecklines: filteredDecklines,
      selectedDeckline,
      selectedDecklineDriverOptions,
      selectedDenominatorDriverOptions,
      decklineType,
      setDecklineType,
      denominatorType,
      setDenominatorType,
      chooseDeckline,
      repositionDeckline,
      isCreatingSubtotal,
      setIsCreatingSubtotal,
      saveSubtotal,
      resolvedNodes,
      subtotalMap,
      subtotalRanges,
      setSubtotalMap,
      setSubtotalRanges,
      updatePageDeckline,
      updateDecklineComments,
      updateDecklinesForPageDriverChanges,
      updateDecklineDriverActiveState,
      updateDecklineDenominatorDriverActiveState,
      updateAllDecklinesDenominatorType,
      insertPageDeckline,
      deletePageDeckline,
      saveDecklineChanges,
      handleSave,
      isSaving: isDecklinesSaving,
      isLoading,
      isError,
      error: error as Error | null,
    }),
    [
      filteredDecklines,
      selectedDeckline,
      selectedDecklineDriverOptions,
      selectedDenominatorDriverOptions,
      decklineType,
      setDecklineType,
      denominatorType,
      setDenominatorType,
      chooseDeckline,
      repositionDeckline,
      isCreatingSubtotal,
      saveSubtotal,
      resolvedNodes,
      subtotalMap,
      subtotalRanges,
      updatePageDeckline,
      updateDecklineComments,
      updateDecklinesForPageDriverChanges,
      updateDecklineDriverActiveState,
      updateDecklineDenominatorDriverActiveState,
      updateAllDecklinesDenominatorType,
      insertPageDeckline,
      deletePageDeckline,
      saveDecklineChanges,
      handleSave,
      isDecklinesSaving,
      isLoading,
      isError,
      error,
    ],
  );

  return <DecklinesContext value={contextValue}>{children}</DecklinesContext>;
};

/**
 * Custom hook to access the Decklines context
 *
 * @returns {DecklinesContextType} The Decklines context value
 */
export const useDecklinesContext = (): DecklinesContextType => {
  return use(DecklinesContext);
};
